import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { IEntityToken } from '../../../API';
import { Stack, TextField } from '@fluentui/react';
import React from 'react';
import Editor, { OnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import ReusableDialog, { IReusableDialogFooterProps, ReusableDialogState } from '../../controls/ReusableDialog';

export enum ExpressionBuilderOutputType {
	String = 'String',
	Boolean = 'Boolean'
}

export class ExpressionBuilderState {

	onUpdate: (e: string) => void;

	public dialog:ReusableDialogState;
	public availableColumns: IEntityToken[] = [];
	outputType: ExpressionBuilderOutputType;
	public propertyFilter: string = '';

	constructor(outputType: ExpressionBuilderOutputType, onUpdate:(e:string) => void) {
		this.dialog = new ReusableDialogState("Expression Editor", 600);
		this.outputType = outputType;
		this.onUpdate = onUpdate;
		
		makeAutoObservable(this);
	}
	
	public expression:string = '';
	public setExpression(e:string) {
		this.expression = e;
	}

	public setAvailableTokens(columns: IEntityToken[]) {
		this.availableColumns = columns;
	}

	public setPropertyFilter(filter: string) {
		this.propertyFilter = filter;
	}
}


export interface IExpressionBuilderProps {
	state: ExpressionBuilderState;
}

class ExpressionBuilder extends React.Component<IExpressionBuilderProps, {}> {
	private editorRef: editor.IStandaloneCodeEditor | null = null;

	componentDidMount(): void {
		
	}

	handleEditorDidMount: OnMount = (editor) => {
		this.editorRef = editor;
	}

	insertProperty = (propertyName: string) => {
		const editor = this.editorRef;
		if (editor) {
			const position = editor.getPosition();
			if (position) {
				const textToInsert = `{{ Item.${propertyName} }}`;
				editor.executeEdits('', [{
					range: {
						startLineNumber: position.lineNumber,
						startColumn: position.column,
						endLineNumber: position.lineNumber,
						endColumn: position.column
					},
					text: textToInsert
				}]);
				editor.setPosition({
					lineNumber: position.lineNumber,
					column: position.column + textToInsert.length
				});
				editor.focus();
			}
		}
	}

	render(): React.ReactNode {
	
		const footerProps: IReusableDialogFooterProps = {
			leftButtons: [],
			rightButtons: [{
				text: 'Cancel',
				onClick: (ev) => {
					this.props.state.dialog.setVisible(false);
				},
			},{
				text: 'Save',
				onClick: (ev) => {
					this.props.state.onUpdate(this.props.state.expression);
					this.props.state.dialog.setVisible(false);
				},
			},],
		};
		
		const availableColumns = (this.props.state.availableColumns || []).filter(c => !c.token.startsWith('_Helm'));
		const sortedColumns = [...availableColumns]
			.sort((a, b) => a.pathDescription.toLowerCase().localeCompare(b.pathDescription.toLowerCase()));

		// Apply property filter
		const filterText = this.props.state.propertyFilter.toLowerCase();
		const filteredColumns = filterText
			? sortedColumns.filter(column => 
				column.pathDescription.toLowerCase().includes(filterText) ||
				column.token.toLowerCase().includes(filterText)
			)
			: sortedColumns;

		return <ReusableDialog state={this.props.state.dialog} footerButtons={footerProps}>
			<style>{`
				.property-item {
					padding: 6px 8px;
					cursor: pointer;
					border-radius: 2px;
					background-color: #f3f2f1;
					font-size: 13px;
					transition: background-color 0.1s;
				}
				.property-item:hover {
					background-color: #edebe9;
				}
			`}</style>
			<Stack horizontal tokens={{ childrenGap: 10 }}>
				<Stack.Item grow={1}>
					<Stack tokens={{ childrenGap: 5 }}>
						<div>Enter a {this.props.state.outputType} expression in C# syntax.</div>

						<Editor
							height={300}
							width={500}
							language="csharp"
							value={this.props.state.expression}
							onChange={(s) => { this.props.state.setExpression(s || '') }}
							onMount={this.handleEditorDidMount}
							options={{
								readOnly: false,
								minimap: { enabled: false },
								wordWrap: 'on',
								lineNumbers: 'on',
							}}
						/>
					</Stack>
				</Stack.Item>
				
				{sortedColumns.length > 0 && (
					<Stack.Item style={{ width: 250, borderLeft: '1px solid #ddd', paddingLeft: 10 }}>
						<Stack tokens={{ childrenGap: 5 }}>
							<div style={{ fontWeight: 600, marginBottom: 5 }}>Available Properties</div>
							<div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
								Click a property to insert it into the expression
							</div>
							<TextField
								placeholder="Filter properties..."
								value={this.props.state.propertyFilter}
								onChange={(_, newValue) => this.props.state.setPropertyFilter(newValue || '')}
								aria-label="Filter properties"
								styles={{
									root: { marginBottom: 10 }
								}}
							/>
							<Stack tokens={{ childrenGap: 2 }} style={{ maxHeight: 300, overflowY: 'auto' }}>
								{filteredColumns.length > 0 ? (
									filteredColumns.map((column) => (
										<div
											key={column.token}
											onClick={() => this.insertProperty(column.token)}
											className="property-item"
											title={`Click to insert {{Item.${column.token}}}`}
										>
											<div style={{ fontWeight: 500 }}>{column.pathDescription}</div>
											<div style={{ fontSize: 11, color: '#605e5c', marginTop: 2 }}>
												{column.token}
											</div>
										</div>
									))
								) : (
									<div style={{ padding: 10, textAlign: 'center', color: '#999', fontSize: 12 }}>
										No properties match "{this.props.state.propertyFilter.slice(0, 50)}{this.props.state.propertyFilter.length > 50 ? '...' : ''}"
									</div>
								)}
							</Stack>
						</Stack>
					</Stack.Item>
				)}

			</Stack>
		
			<div style={{fontSize:10}}>
				Examples for {this.props.state.outputType} expressions:
				{this.getOutputTypeExamples()}
			</div>

		</ReusableDialog>;
	}
	
	getOutputTypeExamples(): React.ReactNode {
		
		if(this.props.state.outputType === ExpressionBuilderOutputType.String) {
			return (
				<>
					<div><code>{`{{Item.ID}} - {{Item.Title}}`}</code></div>
				</>
			);
		} else if( this.props.state.outputType === ExpressionBuilderOutputType.Boolean) {
			return (
				<>
					<div><code>{`"{{Item.Status}}" == "Active"`}</code></div>
					<div><code>{`!string.IsNullOrEmpty("{{Item.URL}}")`}</code></div>
					<div><code>{`{{Item.IsActive}}`}</code></div>
					<div><code>{`{{Item.Count}} > 0`}</code></div>
				</>
			);
		}
	}
}


export default observer(ExpressionBuilder);
